import { Logo } from '@vrplatform/components-legacy';
import { formatCurrency, utc } from '@vrplatform/utils';
import { formatOwnerName } from '@vrplatform/ui-common';
import {
  Anchor,
  Box,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  rem,
} from '@mantine/core';

type SummaryLine = {
  name: string;
  text: string;
  props?: {
    separated?: boolean;
  };
};

type TeamInfo = {
  billingAddress?: {
    full?: string | null;
  } | null;
  email?: string | null;
  logo?: string | null;
  name?: string | null;
  phone?: string | null;
  taxId?: string | null;
};

type ContactInfo = {
  address?: {
    full?: string | null;
  } | null;
  companyType?: string | null;
  email?: string | null;
  name?: string | null;
  phone?: string | null;
};

type ListingInfo = {
  address?: {
    full?: string | null;
  } | null;
  id?: string | null;
  name?: string | null;
};

type PayoutInfo = {
  date: string;
  id?: string | null;
  total: number;
};

export const StatementSummaryHeader = ({
  contact,
  currency,
  lines,
  listing,
  mode,
  payouts = [],
  summaryTitle,
  team,
  onOpenListing,
  onOpenPayout,
}: {
  contact: ContactInfo;
  currency?: string;
  lines: SummaryLine[];
  listing: ListingInfo | null;
  mode: 'owner' | 'propertyManager';
  payouts?: PayoutInfo[];
  summaryTitle: string;
  team: TeamInfo;
  onOpenListing?: (listingId: string) => void;
  onOpenPayout?: (payoutId: string) => void;
}) => {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing.xl,
        paddingTop: theme.spacing.lg,
        marginBottom: theme.spacing.xl,
        '& > *': {
          width: '100%',
          flexGrow: 1,
        },
      })}
    >
      <InfoCard
        contact={contact}
        listing={listing}
        mode={mode}
        team={team}
        onOpenListing={onOpenListing}
      />
      <Box
        sx={(theme) => ({
          flex: 0.7,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
        })}
      >
        <SummaryCard summaryTitle={summaryTitle} lines={lines} />
        {mode === 'propertyManager' && payouts.length > 0 ? (
          <PayoutsCard
            currency={currency}
            payouts={payouts}
            onOpenPayout={onOpenPayout}
          />
        ) : null}
      </Box>
    </Box>
  );
};

const InfoCard = ({
  contact,
  listing,
  mode,
  team,
  onOpenListing,
}: {
  contact: ContactInfo;
  listing: ListingInfo | null;
  mode: 'owner' | 'propertyManager';
  team: TeamInfo;
  onOpenListing?: (listingId: string) => void;
}) => {
  const ownerName = contact.companyType
    ? null
    : formatOwnerName(contact) || '-';
  const ownerCompany = contact.companyType ? contact.name : null;

  return (
    <Card
      withBorder
      radius="md"
      sx={(theme) => ({
        backgroundColor: theme.colors.neutral[0],
        borderColor: theme.colors.neutral[1],
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.lg,
      })}
    >
      <Group>
        <Logo img={team.logo || undefined} width={48} radius={10} />
        <Box>
          <Text component="p" m={0} c="neutral.5" fz={rem(12)} lh={1.4}>
            Property Management
          </Text>
          <Text size="md" fw={500} component="p" m={0}>
            {team.name || '-'}
          </Text>
        </Box>
      </Group>

      <Stack gap={rem(8)} mb="xl">
        <Field label="Address" value={team.billingAddress?.full} />
        <Field label="Email" value={team.email} />
        <Field label="Phone" value={team.phone} />
        <Field label="Tax ID" value={team.taxId} />
      </Stack>

      {listing?.name ? (
        mode === 'propertyManager' && listing.id && onOpenListing ? (
          <Anchor
            component="button"
            size="xs"
            m={0}
            onClick={() => onOpenListing(listing.id!)}
          >
            {listing.name}
          </Anchor>
        ) : (
          <Text size="xs" fw={400} component="p" c="gray" m={0}>
            {listing.name}
          </Text>
        )
      ) : null}
      {listing?.address?.full ? (
        <Text size="md" fw={500} component="p" m={0}>
          {listing.address.full}
        </Text>
      ) : null}

      <Stack gap={rem(8)}>
        <Field label="Owner" value={ownerName} />
        <Field label="Company" value={ownerCompany} />
        <Field label="Address" value={contact.address?.full} />
        <Field label="Email" value={contact.email} />
        <Field label="Phone" value={contact.phone} />
      </Stack>
    </Card>
  );
};

const SummaryCard = ({
  summaryTitle,
  lines,
}: {
  summaryTitle: string;
  lines: SummaryLine[];
}) => {
  return (
    <Card
      p="md"
      radius="md"
      withBorder
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: rem(10),
        backgroundColor: theme.colors.neutral[0],
        borderColor: theme.colors.neutral[1],
      })}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: rem(8) }}>
        <Text component="p" m={0} c="neutral.5" fz={rem(12)} lh={1.4}>
          Statement Summary
        </Text>
        <Text component="p" m={0} c="neutral.8" fw={700} fz={rem(16)} lh={1.4}>
          {summaryTitle}
        </Text>
      </Box>
      <Divider color="neutral.2" />
      {lines.map((line, index) => {
        const normalized = line.name.toLowerCase();
        const isBold =
          [
            'balance start',
            'balance forward',
            'balance end',
            'available balance',
          ].includes(normalized) || index === 0;
        const isPrimary = ['amount due', 'net income', 'earnings'].includes(
          normalized
        );

        return (
          <Box key={line.name}>
            {line.props?.separated ? (
              <Divider color="neutral.2" mb={rem(10)} />
            ) : null}
            <Group justify="space-between" wrap="nowrap" pr={rem(8)}>
              <Text component="p" fw={isBold ? 500 : 400} c="neutral.6" m={0}>
                {line.name}
              </Text>
              <Text
                component="p"
                fw={isBold ? 500 : 400}
                c={isPrimary ? '#1343CC' : 'neutral.6'}
                m={0}
                ta="right"
              >
                {line.text}
              </Text>
            </Group>
          </Box>
        );
      })}
    </Card>
  );
};

const PayoutsCard = ({
  currency,
  payouts,
  onOpenPayout,
}: {
  currency?: string;
  payouts: PayoutInfo[];
  onOpenPayout?: (payoutId: string) => void;
}) => {
  return (
    <Card
      p={rem(16)}
      radius="md"
      withBorder
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: rem(8),
        backgroundColor: theme.colors.blue[0],
        borderColor: theme.colors.blue[1],
      })}
    >
      <Text component="p" m={0} c="neutral.5" fz={rem(12)} lh={1.4}>
        Current statement payments
      </Text>
      <Stack gap={rem(8)}>
        {payouts.map((payout, index) => {
          const label = `Payment on ${utc(payout.date).format('ll')}`;
          const value = formatCurrency(payout.total / 100, currency);
          const key = payout.id || `${payout.date}-${payout.total}-${index}`;

          return (
            <Group key={key} justify="space-between" wrap="nowrap">
              {payout.id && onOpenPayout ? (
                <Anchor
                  component="button"
                  m={0}
                  c="neutral.8"
                  fz={rem(14)}
                  lh={1.4}
                  onClick={() => onOpenPayout(payout.id!)}
                >
                  {label}
                </Anchor>
              ) : (
                <Text component="p" m={0} c="neutral.8" fz={rem(14)} lh={1.4}>
                  {label}
                </Text>
              )}
              <Text
                component="p"
                m={0}
                c="neutral.8"
                fz={rem(14)}
                lh={1.4}
                ta="right"
              >
                {value}
              </Text>
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
};

const Field = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) {
    return null;
  }

  return (
    <Box>
      <Text size="xs" c="gray">
        {label}
      </Text>
      <Text>{value}</Text>
    </Box>
  );
};
